import { formatAmount, formatShareBps, USD } from "@loot-os/api-contract";
import {
  ECONOMICS_HINTS,
  FEE_COLOR,
  MARGIN_COLOR,
  marginBarShare,
  marginScaleBps,
  type ShopifyEconomicsView,
  VOLUME_SPLIT_HINTS,
  type VolumeSplitKey,
  volumeSplit,
} from "@loot-os/api-contract/shopifyOverview";
import { describeRules, summarizeRules } from "@loot-os/api-contract/shopifyRuleBoxForm";
import type { ShopifyAdminAppRouter } from "@loot-os/functions/src/dashboard-api/trpc/shopifyAdmin/router.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { type ReactNode, useId } from "react";
import { useSetBoxOnSale } from "./boxSale";
import { boxStanding, HOME_BOX_LIMIT, tallyTotal } from "./home";
import { RuleBoxBuilder } from "./RuleBoxBuilder";
import { formatExact } from "./refreshTime";
import { SellInStoreSwitch } from "./SellInStoreSwitch";
import { navigate } from "./screens";
import { TABLE, TD, TH } from "./tableStyles";
import { useTRPC } from "./trpc";

type Outputs = inferRouterOutputs<ShopifyAdminAppRouter>;
type Summary = NonNullable<Outputs["status"]["summary"]>;
type BoxRow = Outputs["boxes"]["list"][number];

/** Every figure here is USD, which every shop the app serves sells in. */
const usd = (cents: number) => formatAmount(cents, USD);

/** The bars' neutral fills on the card: the empty track and the product paid out. */
const TRACK = "#ebebeb";
const PAYOUT = "#b5b5b5";

/**
 * A headline figure, at the size the dashboard's overview draws it: Polaris has no display text,
 * and `s-heading` there is no larger than its label. The admin's text color and font.
 */
const FIGURE = {
  fontSize: 20,
  lineHeight: "28px",
  fontWeight: 650,
  color: "#303030",
  fontVariantNumeric: "tabular-nums",
} as const;

/** Fixed columns, so a long box name never pushes the price and status apart. */
const FIXED_TABLE = { ...TABLE, tableLayout: "fixed" } as const;
const ONE_LINE = { overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" } as const;

/**
 * Home's actions in the page's title bar, so rendered as `s-page`'s own children: "Create a
 * box", which opens the builder on Home (disabled until the setup guide is done), and a sync of the
 * store's products.
 */
export function HomeActions({
  canCreate,
  onCreateBox,
}: {
  canCreate: boolean;
  onCreateBox: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const sync = useMutation(
    trpc.catalog.sync.mutationOptions({
      onSuccess: (result) => {
        shopify.toast.show(
          result.complete
            ? `Synced ${result.variants} variants.`
            : "Still syncing; it finishes within a few minutes.",
        );
        void queryClient.invalidateQueries({ queryKey: trpc.status.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.catalog.pathKey() });
      },
    }),
  );
  return (
    <>
      <s-button
        slot="primary-action"
        variant="primary"
        disabled={!canCreate || undefined}
        onClick={onCreateBox}
      >
        Create a box
      </s-button>
      <s-button
        slot="secondary-actions"
        disabled={sync.isPending || undefined}
        onClick={() => sync.mutate()}
      >
        {sync.isPending ? "Syncing…" : "Sync products"}
      </s-button>
    </>
  );
}

/**
 * The app's root: the tenant dashboard's Shopify overview (`ShopifyOverview.tsx`) in the admin's
 * own components, with box creation beside it. The shop's lifetime sales (volume, projected and
 * realized margin, the Loot Labs fee, as `getShopifyEconomics` defines them) with where the volume
 * goes and the two margins drawn as bars, in a card, the one card of the otherwise flat admin, so
 * the figures do not run into the page; then the newest boxes and where each stands. Before the
 * first purchase only the boxes show, since every figure would be zero. `creating` (the title
 * bar's "Create a box") opens the builder in place; a box it creates is shown on Boxes, where its
 * "Sell in store" switch is.
 */
export function HomeScreen({
  canCreate,
  creating,
  onCreateBox,
  onCloseBuilder,
}: {
  canCreate: boolean;
  creating: boolean;
  onCreateBox: () => void;
  onCloseBuilder: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  // Home's own observer of `status`, so every visit reads the figures again.
  const status = useQuery(trpc.status.queryOptions());
  const boxes = useQuery(trpc.boxes.list.queryOptions());

  if (creating) {
    return (
      <RuleBoxBuilder
        start={null}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: trpc.boxes.list.queryKey() });
          onCloseBuilder();
          navigate("/boxes");
        }}
        onCancel={onCloseBuilder}
      />
    );
  }

  const summary = status.data?.summary;
  const sold = summary ? tallyTotal(summary.purchases) > 0 : false;
  return (
    <>
      {summary && sold && (
        <SalesOverview summary={summary} lastCatalogSyncAt={status.data?.lastCatalogSyncAt} />
      )}
      <BoxesOverview
        rows={boxes.data}
        loading={boxes.isLoading}
        failed={boxes.isError}
        sold={sold}
        canCreate={canCreate}
        onCreateBox={onCreateBox}
      />
    </>
  );
}

/**
 * The lifetime figures, then where the volume goes and the margin expected against the margin
 * kept, in one card (`s-section`), as the dashboard draws them.
 */
function SalesOverview({
  summary,
  lastCatalogSyncAt,
}: {
  summary: Summary;
  lastCatalogSyncAt: string | null | undefined;
}) {
  const { economics } = summary;
  const synced = lastCatalogSyncAt ? formatExact(new Date(lastCatalogSyncAt)) : "never";
  const fee = economics.platformFee;
  return (
    <s-section heading="Sales">
      <s-stack gap="base">
        <s-text color="subdued">
          All time, USD, refunds excluded · {summary.variants.toLocaleString()} products, synced{" "}
          {synced}
        </s-text>
        <s-query-container>
          <s-grid
            gridTemplateColumns="@container (inline-size <= 560px) 1fr 1fr, 1fr 1fr 1fr 1fr"
            gap="large"
          >
            <Figure
              label="Total volume"
              hint={ECONOMICS_HINTS.volume}
              value={usd(economics.volume.cents)}
              note={`${economics.volume.units.toLocaleString()} boxes sold`}
            />
            <Figure
              label="Projected margin"
              hint={ECONOMICS_HINTS.projectedMargin}
              value={usd(economics.projectedMargin.cents)}
              percent={economics.projectedMargin.bps}
              note="Your boxes' margin on every box sold"
            />
            <Figure
              label="Realized margin"
              hint={ECONOMICS_HINTS.realizedMargin}
              value={usd(economics.realizedMargin.cents)}
              percent={economics.realizedMargin.bps}
              note={`On ${economics.realizedMargin.settledUnits.toLocaleString()} settled boxes`}
            />
            <Figure
              label="Loot Labs volume fee"
              hint={ECONOMICS_HINTS.billedFee}
              value={usd(fee.accruedCents)}
              note={
                fee.pendingCents > 0
                  ? `${formatShareBps(fee.shareBps)} of settled boxes, ${usd(fee.pendingCents)} not yet billed`
                  : `${formatShareBps(fee.shareBps)} of settled boxes, on your Shopify invoice`
              }
            />
          </s-grid>
        </s-query-container>
        <s-divider />
        <s-query-container>
          <s-grid gridTemplateColumns="@container (inline-size <= 640px) 1fr, 1fr 1fr" gap="large">
            <VolumeSplitChart economics={economics} />
            <MarginChart economics={economics} />
          </s-grid>
        </s-query-container>
      </s-stack>
    </s-section>
  );
}

/** One headline figure with its percent beside it and a line beneath. */
function Figure({
  label,
  hint,
  value,
  percent,
  note,
}: {
  label: string;
  hint: string;
  value: string;
  percent?: number | null;
  note: string;
}) {
  return (
    <s-stack gap="small-300">
      <Term hint={hint} color="subdued">
        {label}
      </Term>
      <s-stack direction="inline" gap="small-200" alignItems="baseline">
        <span style={FIGURE}>{value}</span>
        {percent !== undefined && (
          <s-text color="subdued" fontVariantNumeric="tabular-nums">
            {percent === null ? "—" : formatShareBps(percent)}
          </s-text>
        )}
      </s-stack>
      <s-text color="subdued">{note}</s-text>
    </s-stack>
  );
}

const SEGMENT_COLOR: Record<VolumeSplitKey, string> = {
  payout: PAYOUT,
  fee: FEE_COLOR,
  margin: MARGIN_COLOR,
};

/** Every dollar of volume at the projected margin: product paid out, the fee, the margin kept. */
function VolumeSplitChart({ economics }: { economics: ShopifyEconomicsView }) {
  const { segments, netMarginCents } = volumeSplit(economics);
  const drawn = segments.filter((s) => s.share > 0);
  return (
    <s-stack gap="base">
      <s-text type="strong">Where the volume goes, projected</s-text>
      <div
        style={{
          display: "flex",
          gap: 2,
          height: 12,
          overflow: "hidden",
          borderRadius: 999,
          background: TRACK,
        }}
      >
        {drawn.map((s) => (
          <div
            key={s.key}
            style={{ width: `${s.share * 100}%`, background: SEGMENT_COLOR[s.key] }}
            title={`${s.label}: ${usd(s.cents)} (${percent(s.share)})`}
          />
        ))}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr) auto 3rem",
          alignItems: "center",
          columnGap: 12,
          rowGap: 6,
        }}
      >
        {segments.map((s) => (
          <div key={s.key} style={{ display: "contents" }}>
            <span
              aria-hidden
              style={{ width: 10, height: 10, borderRadius: 2, background: SEGMENT_COLOR[s.key] }}
            />
            <Term hint={VOLUME_SPLIT_HINTS[s.key]} color="subdued">
              {s.label}
            </Term>
            <s-text fontVariantNumeric="tabular-nums">
              {usd(s.key === "margin" ? netMarginCents : s.cents)}
            </s-text>
            <div style={{ textAlign: "right" }}>
              <s-text color="subdued" fontVariantNumeric="tabular-nums">
                {economics.volume.cents > 0 ? percent(s.share) : "—"}
              </s-text>
            </div>
          </div>
        ))}
      </div>
    </s-stack>
  );
}

/** The two margins as percents on one scale, the projection lighter than what was kept. */
function MarginChart({ economics }: { economics: ShopifyEconomicsView }) {
  const scale = marginScaleBps(economics);
  const rows = [
    {
      label: "Projected",
      hint: ECONOMICS_HINTS.projectedPercent,
      bps: economics.projectedMargin.bps,
      opacity: 0.45,
    },
    {
      label: "Realized",
      hint: ECONOMICS_HINTS.realizedPercent,
      bps: economics.realizedMargin.bps,
      opacity: 1,
    },
  ];
  const columns = {
    display: "grid",
    gridTemplateColumns: "5rem minmax(0, 1fr) 3.5rem",
    alignItems: "center",
    columnGap: 12,
  } as const;
  return (
    <s-stack gap="base">
      <s-text type="strong">Margin, projected against realized</s-text>
      <div style={{ display: "grid", rowGap: 10 }}>
        {rows.map((row) => (
          <div key={row.label} style={columns}>
            <Term hint={row.hint} color="subdued">
              {row.label}
            </Term>
            <Bar
              share={marginBarShare(row.bps, scale)}
              color={MARGIN_COLOR}
              opacity={row.opacity}
            />
            <div style={{ textAlign: "right" }}>
              <s-text fontVariantNumeric="tabular-nums">
                {row.bps === null ? "—" : formatShareBps(row.bps)}
              </s-text>
            </div>
          </div>
        ))}
        <div style={columns}>
          <span />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <s-text color="subdued">0%</s-text>
            <s-text color="subdued">{formatShareBps(scale)}</s-text>
          </div>
          <span />
        </div>
      </div>
    </s-stack>
  );
}

/**
 * The newest boxes, each with where it stands and its "Sell in store" switch, linking to Boxes for
 * everything else; with no box yet, what a box is and a button that makes the first. Before any
 * sale it also says where the sales will show.
 */
function BoxesOverview({
  rows,
  loading,
  failed,
  sold,
  canCreate,
  onCreateBox,
}: {
  rows: BoxRow[] | undefined;
  loading: boolean;
  failed: boolean;
  sold: boolean;
  canCreate: boolean;
  onCreateBox: () => void;
}) {
  const shown = (rows ?? []).slice(0, HOME_BOX_LIMIT);
  const setOnSale = useSetBoxOnSale();
  return (
    <s-stack gap="base">
      <s-heading>Boxes</s-heading>
      {loading ? (
        <s-paragraph>Loading your boxes…</s-paragraph>
      ) : failed || !rows ? (
        <s-banner tone="critical">Your boxes could not be loaded. Try again in a moment.</s-banner>
      ) : rows.length === 0 ? (
        <>
          <s-paragraph>
            A loot box is a product your customers buy without knowing which item is inside. Tell us
            which of your products can be inside (by tag, vendor, type and price) and the margin you
            want, and we'll price the box and set the odds. Every day we rebuild it from your store,
            so sold-out products drop out and new ones join.
          </s-paragraph>
          <s-stack direction="inline">
            <s-button variant="primary" disabled={!canCreate || undefined} onClick={onCreateBox}>
              Create your first box
            </s-button>
          </s-stack>
        </>
      ) : (
        <>
          {!sold && (
            <s-paragraph color="subdued">
              Your sales show here once a customer buys a box. Switch on "Sell in store" to list a
              box like any other product.
            </s-paragraph>
          )}
          <table style={FIXED_TABLE}>
            <colgroup>
              <col />
              <col style={{ width: 128 }} />
              <col style={{ width: 112 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={TH}>Box</th>
                <th style={TH}>Status</th>
                <th style={TH}>Sell in store</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((box) => {
                const standing = boxStanding(box);
                return (
                  <tr key={box.id}>
                    <td style={TD}>
                      <s-grid
                        gridTemplateColumns="auto minmax(0, 1fr)"
                        gap="base"
                        alignItems="center"
                      >
                        <s-thumbnail
                          src={box.imageUrl || undefined}
                          alt={box.imageUrl ? box.name : "No image"}
                          size="small"
                        />
                        <s-stack gap="small-300">
                          <div style={ONE_LINE} title={box.name}>
                            <s-link href="/boxes">{box.name}</s-link>
                          </div>
                          {/* The price, then the rules on one line; the whole of them is the title. */}
                          <div style={ONE_LINE} title={describeRules(box.rule.config.rules)}>
                            <s-text color="subdued">
                              {box.currentVersion > 0 ? `${usd(box.priceCents)} · ` : ""}
                              {summarizeRules(box.rule.config.rules)}
                            </s-text>
                          </div>
                        </s-stack>
                      </s-grid>
                    </td>
                    <td style={TD}>
                      <s-badge tone={standing.tone}>{standing.label}</s-badge>
                    </td>
                    <td style={TD}>
                      <SellInStoreSwitch box={box} setOnSale={setOnSale} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <s-stack direction="inline">
            <s-link href="/boxes">
              {rows.length > shown.length ? `View all ${rows.length} boxes` : "Manage boxes"}
            </s-link>
          </s-stack>
        </>
      )}
    </s-stack>
  );
}

/** A bar of `share` (0–1) of its track; nothing drawn at 0. */
function Bar({ share, color, opacity = 1 }: { share: number; color: string; opacity?: number }) {
  return (
    <div style={{ height: 12, borderRadius: 999, background: TRACK }}>
      {share > 0 && (
        <div
          style={{
            height: "100%",
            minWidth: 4,
            width: `${share * 100}%`,
            borderRadius: 999,
            background: color,
            opacity,
          }}
        />
      )}
    </div>
  );
}

/** A label with a dotted underline that explains itself in a tooltip on hover or focus. */
function Term({ hint, color, children }: { hint: string; color?: "subdued"; children: ReactNode }) {
  // React's ids carry characters a selector would need escaped; Polaris looks the tooltip up by id.
  const id = `term-${useId().replace(/[^\w-]/g, "")}`;
  return (
    <span
      style={{
        width: "fit-content",
        cursor: "help",
        textDecoration: "underline dotted #8a8a8a",
        textUnderlineOffset: 4,
      }}
    >
      <s-text interestFor={id} color={color}>
        {children}
      </s-text>
      <s-tooltip id={id}>{hint}</s-tooltip>
    </span>
  );
}

function percent(share: number): string {
  return `${Math.round(share * 100)}%`;
}
